import { useQuery } from '@apollo/client';
import { isNil } from '@sixfold/typed-primitives';
import { useMemo } from 'react';

import { MobileTourSignals } from './diagnostics';
import { mobileDeviceBreakdownQuery, mobileTourSignalsQuery } from './graphql';
import { Breakdown, LogEntry } from './types';
import {
  MobileDeviceBreakdownQuery,
  MobileDeviceBreakdownQueryVariables,
  MobileInvitationStatus,
  MobileTourSignalsQuery,
  MobileTourSignalsQueryVariables,
} from '../lib/graphql';

const BREAKDOWN_DAYS = 30;

type SignalsTour = NonNullable<MobileTourSignalsQuery['tour']>;

const INVITATION_LOG: Record<MobileInvitationStatus, { name: string; severity: LogEntry['severity'] }> = {
  COMPOSED: { name: 'Invitation SMS composed', severity: 'success' },
  NOT_COMPOSED: { name: 'Invitation SMS not composed', severity: 'danger' },
  UNKNOWN: { name: 'Invitation requested', severity: 'info' },
};

const SIGNIFICANT_APP_EVENTS = [
  'LOGIN_SUCCESS',
  'LOGIN_FAILED',
  'DEEP_LINK_AUTO_LOGIN_SUCCESS',
  'DEEP_LINK_AUTO_LOGIN_FAILED',
  'LOCATION_PERMISSION_GRANTED',
  'LOCATION_PERMISSION_DENIED',
];

const FAILURE_EVENTS = ['LOGIN_FAILED', 'DEEP_LINK_AUTO_LOGIN_FAILED', 'LOCATION_PERMISSION_DENIED'];

export const buildSignalLogs = (tour: SignalsTour | null | undefined): LogEntry[] => {
  if (!tour) {
    return [];
  }
  const invitations = (tour.mobileInvitations ?? []).flatMap((invitation): LogEntry[] => {
    const label = INVITATION_LOG[invitation.status];
    return [
      {
        id: `invitation:${invitation.notificationId}`,
        timestamp: invitation.sentAt,
        source: 'Notification',
        name: label.name,
        details: invitation.phoneNumber === null ? '' : `Phone ${invitation.phoneNumber}`,
        significant: true,
        severity: label.severity,
      },
    ];
  });
  const appEvents = (tour.mobileAppEvents ?? []).map((event, index): LogEntry => ({
    id: `app:${event.name}:${event.timestamp}:${index}`,
    timestamp: event.timestamp,
    source: 'Driver app',
    name: event.name,
    details: [
      ...(event.appVersion === null ? [] : [`version=${event.appVersion}`]),
      ...event.params.filter(({ key }) => key !== 'tour_id').map(({ key, value }) => `${key}=${value}`),
    ].join(', '),
    significant: SIGNIFICANT_APP_EVENTS.includes(event.name),
    severity: FAILURE_EVENTS.includes(event.name) ? 'warning' : 'info',
  }));
  return [...invitations, ...appEvents];
};

export const useMobileTourSignals = (tourId: number) => {
  const { data, loading, error } = useQuery<MobileTourSignalsQuery, MobileTourSignalsQueryVariables>(
    mobileTourSignalsQuery,
    { variables: { tour_id: tourId }, errorPolicy: 'all' },
  );
  const tour = data?.tour;
  const signals = useMemo<MobileTourSignals | undefined>(() => {
    if (loading) {
      return undefined;
    }
    return {
      invitations: tour?.mobileInvitations ?? null,
      appEvents: tour?.mobileAppEvents ?? null,
    };
  }, [loading, tour]);
  const logs = useMemo(() => buildSignalLogs(tour), [tour]);
  return { signals, session: tour?.mobileSession ?? null, logs, loading, error };
};

const toBreakdown = (entries: { label: string; devices: number }[]): Breakdown[] =>
  entries.map(({ label, devices }) => ({ label, value: devices }));

export const useMobileDeviceBreakdown = () => {
  const { data, loading, error } = useQuery<MobileDeviceBreakdownQuery, MobileDeviceBreakdownQueryVariables>(
    mobileDeviceBreakdownQuery,
    { variables: { days: BREAKDOWN_DAYS } },
  );
  const breakdown = data?.mobileDeviceBreakdown;
  return {
    appVersions: toBreakdown(breakdown?.appVersions ?? []),
    operatingSystems: toBreakdown(breakdown?.operatingSystems ?? []),
    available: !isNil(breakdown),
    loading,
    error,
    days: BREAKDOWN_DAYS,
  };
};
